import { useEffect, useRef } from 'react';
import type { DesktopCommand, DesktopState } from '@kiipu/desktop-protocol';
import { isDesktop, usePlatform } from './client-platform';

export const workspaceCommands: readonly DesktopCommand[] = [
  'save',
  'refresh',
  'toggle-sidebar',
  'previous',
  'next',
];
export const readerCommands: readonly DesktopCommand[] = [
  'favorite',
  'find-in-content',
  'refresh',
  'remember-position',
];
const maximumPendingCommands = 32;
let identity = '';
const pending: DesktopCommand[] = [];
const subscribers = new Set<{
  owner: string;
  commands: readonly DesktopCommand[];
  receive: (command: DesktopCommand) => void;
}>();
const owner = (state: DesktopState | null) =>
  state ? JSON.stringify([state.apiOrigin, state.user?.id ?? null, state.sessionGeneration]) : '';
export function setDesktopCommandIdentity(state: DesktopState) {
  const next = owner(state);
  if (next !== identity) {
    identity = next;
    pending.length = 0;
  }
}
export function dispatchDesktopCommand(command: DesktopCommand) {
  const active = [...subscribers].filter(
    (item) => item.owner === identity && item.commands.includes(command),
  );
  if (active.length) {
    for (const item of active) item.receive(command);
    return;
  }
  const previous = pending.indexOf(command);
  if (previous >= 0) pending.splice(previous, 1);
  pending.push(command);
  if (pending.length > maximumPendingCommands) pending.shift();
}
export function subscribeDesktopCommands(
  commands: readonly DesktopCommand[],
  receive: (command: DesktopCommand) => void,
) {
  const subscription = { owner: identity, commands, receive };
  subscribers.add(subscription);
  for (const command of [...pending]) {
    if (!commands.includes(command)) continue;
    pending.splice(pending.indexOf(command), 1);
    receive(command);
  }
  return () => {
    subscribers.delete(subscription);
  };
}
/** The subscription itself is the readiness signal; no timer guesses when React has mounted. */
export function useDesktopCommands(
  commands: readonly DesktopCommand[],
  receive: (command: DesktopCommand) => void,
  ready = true,
) {
  const state = usePlatform();
  const latest = useRef(receive);
  latest.current = receive;
  const identity = owner(state);
  useEffect(() => {
    if (!ready || !isDesktop()) return;
    return subscribeDesktopCommands(commands, (command) => latest.current(command));
  }, [commands, ready, identity]);
}
